import { getArticleUrl, getCategoryUrl } from '@/utils/academy';
import {
  Heading,
  ListItem,
  OrderedList,
  Text,
  UnorderedList,
  Link,
  TableContainer,
  Table,
  Tr,
  Th,
  Td,
  Thead,
  VStack,
} from '@chakra-ui/react';
import {
  ComponentRichText,
  ComponentRichTextContentLinks,
  Entry,
} from '@cms/graphql/types.generated';
import { documentToReactComponents, Options } from '@contentful/rich-text-react-renderer';
import { Block, BLOCKS, Inline, INLINES, MARKS } from '@contentful/rich-text-types';
import { ReactNode } from 'react';
import NextLink from 'next/link';
interface ArticleRichTextProps extends Pick<ComponentRichText, 'content'> {
  elementMap?: Options;
}

interface OptionsMapParams {
  content: ComponentRichText['content'];
  elementMap?: Options;
}

type HyperlinkEntryWithSlug = Entry & {
  urlSlug: string;
  __typename: 'PageAcademyArticle' | 'PageAcademyCategory' | 'PageAcademyOverview';
};
type LinkResolverArgs = { node: Inline; children: ReactNode; links: ComponentRichTextContentLinks };
type LinkResolverReturn = JSX.Element | null;

/**
 * Regular hyperlink resolver. If a link starts with 'http' it will be considered an external link. All other values
 * are considered internal links and will be wrapped with NextLink.
 * @param param0
 * @returns
 */
const resolveHyperlink = ({ node, children }: LinkResolverArgs): LinkResolverReturn => {
  const { uri: href } = node.data;

  if (href.startsWith('http')) {
    return (
      <Link href={href} isExternal>
        {children}
      </Link>
    );
  }

  return (
    <NextLink href={href} passHref legacyBehavior>
      <Link>{children}</Link>
    </NextLink>
  );
};

/**
 * Resolves links to other Academy entries (articles and categrory pages) and the Academy overview page itself.
 * @param param0
 * @returns
 */
const resolveEntryHyperlink = ({ node, children, links }: LinkResolverArgs): LinkResolverReturn => {
  if (!node?.data?.target?.sys.id || !links?.entries?.hyperlink?.length) return null;

  const linkedItem: HyperlinkEntryWithSlug = links.entries.hyperlink.find(
    link => link?.sys.id === node?.data?.target?.sys.id,
  ) as HyperlinkEntryWithSlug;

  if (!linkedItem) return null;

  const href = {
    PageAcademyArticle: getArticleUrl(linkedItem.urlSlug),
    PageAcademyCategory: getCategoryUrl(linkedItem.urlSlug),
    PageAcademyOverview: `/academy`,
  }[linkedItem.__typename];

  if (!href) return null;

  return (
    <NextLink href={href} passHref legacyBehavior>
      <Link>{children}</Link>
    </NextLink>
  );
};

/**
 * Resolves inline links in rich text entries to either other entries, or internal and external url's
 * @param links
 * @returns
 */
const richTextLinkResolver =
  (links: ComponentRichTextContentLinks) => (node: Inline, children: ReactNode) => {
    const resolverMap: {
      [key in INLINES]: (args: LinkResolverArgs) => LinkResolverReturn;
    } = {
      [INLINES.HYPERLINK]: resolveHyperlink,
      [INLINES.ENTRY_HYPERLINK]: resolveEntryHyperlink,
      [INLINES.ASSET_HYPERLINK]: () => null, // not implemented
      [INLINES.EMBEDDED_ENTRY]: () => null, // not implemented
    };

    if (!(node.nodeType in resolverMap)) return children;

    return resolverMap[node.nodeType]({ node, children, links }) || children;
  };

const getOptionsMap = ({ content, elementMap }: OptionsMapParams): Options => ({
  renderNode: {
    [BLOCKS.HEADING_1]: (_node, children: ReactNode) => (
      <Heading as="h1" size="md">
        {children}
      </Heading>
    ),
    [BLOCKS.HEADING_2]: (_node, children: ReactNode) => <Heading size="md">{children}</Heading>,
    [BLOCKS.HEADING_3]: (_node, children: ReactNode) => (
      <Heading as="h3" size="md">
        {children}
      </Heading>
    ),
    [BLOCKS.HEADING_4]: (_node, children: ReactNode) => (
      <Heading as="h4" size="md">
        {children}
      </Heading>
    ),
    [BLOCKS.HEADING_5]: (_node, children: ReactNode) => (
      <Heading as="h5" size="md">
        {children}
      </Heading>
    ),
    [BLOCKS.HEADING_6]: (_node, children: ReactNode) => (
      <Heading as="h6" size="md">
        {children}
      </Heading>
    ),
    [BLOCKS.PARAGRAPH]: (_node, children: ReactNode) => (
      <Text whiteSpace="pre-wrap">{children}</Text>
    ),
    [BLOCKS.OL_LIST]: (_node, children: ReactNode) => <OrderedList>{children}</OrderedList>,
    [BLOCKS.UL_LIST]: (_node, children: ReactNode) => <UnorderedList>{children}</UnorderedList>,
    [BLOCKS.LIST_ITEM]: (_node, children: ReactNode) => <ListItem>{children}</ListItem>,
    [BLOCKS.TABLE]: (_node, children: ReactNode) => (
      <TableContainer>
        <Table>{children}</Table>
      </TableContainer>
    ),
    [BLOCKS.TABLE_ROW]: (_node: Block, children: ReactNode) => {
      if (_node.content.every(node => node.nodeType === BLOCKS.TABLE_HEADER_CELL)) {
        return (
          <Thead>
            <Tr>{children}</Tr>
          </Thead>
        );
      }

      return <Tr>{children}</Tr>;
    },
    [BLOCKS.TABLE_HEADER_CELL]: (_node, children: ReactNode) => <Th>{children}</Th>,
    [BLOCKS.TABLE_CELL]: (_node, children: ReactNode) => <Td>{children}</Td>,
    [INLINES.HYPERLINK]: content.links && richTextLinkResolver(content.links),
    [INLINES.ENTRY_HYPERLINK]: content.links && richTextLinkResolver(content.links),
    ...elementMap.renderNode,
  },
  renderMark: {
    [MARKS.BOLD]: (text: ReactNode) => (
      <Text as="strong" fontWeight="bold">
        {text}
      </Text>
    ),
    [MARKS.ITALIC]: (text: ReactNode) => (
      <Text as="span" fontStyle="italic" color="desiredDawn">
        {text}
      </Text>
    ),
    [MARKS.UNDERLINE]: (text: ReactNode) => (
      <Text as="span" textDecoration="underline">
        {text}
      </Text>
    ),
    ...elementMap.renderMark,
  },
});

export const ArticleRichText = ({ content, elementMap }: ArticleRichTextProps) => {
  const options = getOptionsMap({ content, elementMap });

  return (
    <VStack
      mt="md"
      spacing="md"
      justify="left"
      align="left"
      sx={{
        color: 'desiredDawn',
        h3: {
          fontSize: 'md',
          lineHeight: 1.1,
          fontWeight: 'bold',
          color: 'white',
        },
        ul: {
          marginInlineStart: 'lg',
          paddingLeft: '2.2rem',
          color: 'muted',
          'li::marker': {
            fontSize: 'lg',
            color: 'lightPurple',
          },
          li: {
            marginBottom: '1.3rem',
          },
        },
        ol: {
          marginInlineStart: 'lg',
          paddingLeft: '2.7rem',
          li: {
            paddingLeft: '0.5rem',
            marginBottom: '1.3rem',
          },
        },
      }}>
      {documentToReactComponents(content.json, options)}
    </VStack>
  );
};
